package org.zq.astrock_ai.ui.research.gushi.data

import org.zq.astrock_ai.storage.HoldingsLocalStorage
import org.zq.astrock_ai.ui.research.gushi.model.HoldingPosition

object HoldingsStore {
    fun load(): Map<String, HoldingPosition> =
        decodeHoldings(HoldingsLocalStorage.load())

    fun save(holdings: Map<String, HoldingPosition>) {
        HoldingsLocalStorage.save(encodeHoldings(holdings))
    }
}

/**
 * 将纸面流水虚拟成交应用到持仓：
 * BUY 建仓/加仓（默认 100 股），SELL 减仓或清仓。
 */
fun applyPaperTradeToHoldings(
    holdings: Map<String, HoldingPosition>,
    entry: PaperTradeEntry,
    lotSize: Int = 100,
): Map<String, HoldingPosition> {
    val code = entry.code.filter { it.isDigit() }.takeLast(6)
    val qty = when {
        entry.quantity > 0 -> entry.quantity
        lotSize > 0 -> lotSize
        else -> 100
    }
    if (code.length != 6 || qty <= 0) return holdings
    return when (entry.side.uppercase()) {
        "BUY" -> {
            val price = entry.price
            if (price <= 0) return holdings
            val prev = holdings[code]
            if (prev == null) {
                holdings + (code to HoldingPosition(
                    stockCode = code,
                    stockName = entry.name.ifBlank { code },
                    costPrice = price,
                    quantity = qty,
                ))
            } else {
                val newQty = prev.quantity + qty
                val newCost = (prev.costPrice * prev.quantity + price * qty) / newQty
                holdings + (code to prev.copy(
                    stockName = entry.name.ifBlank { prev.stockName },
                    costPrice = newCost,
                    quantity = newQty,
                ))
            }
        }
        "SELL" -> {
            val prev = holdings[code] ?: return holdings
            val remain = prev.quantity - qty
            if (remain <= 0) holdings - code
            else holdings + (code to prev.copy(quantity = remain))
        }
        else -> holdings
    }
}

internal fun encodeHoldings(holdings: Map<String, HoldingPosition>): String =
    holdings.values
        .sortedBy { it.stockCode }
        .joinToString("\n") { h ->
            listOf(
                h.stockCode,
                h.stockName.replace('\t', ' ').replace('\n', ' '),
                h.costPrice.toString(),
                h.quantity.toString(),
                h.alertPrice?.toString().orEmpty(),
                h.alertPnlPct?.toString().orEmpty(),
            ).joinToString("\t")
        }

internal fun decodeHoldings(raw: String?): Map<String, HoldingPosition> =
    raw.orEmpty()
        .lineSequence()
        .mapNotNull { line ->
            val parts = line.split('\t')
            if (parts.size < 4) return@mapNotNull null
            val code = parts[0].filter { it.isDigit() }.takeLast(6)
            if (code.length != 6) return@mapNotNull null
            val cost = parts[2].toDoubleOrNull() ?: return@mapNotNull null
            val qty = parts[3].toIntOrNull() ?: return@mapNotNull null
            if (qty <= 0 || cost <= 0) return@mapNotNull null
            HoldingPosition(
                stockCode = code,
                stockName = parts[1],
                costPrice = cost,
                quantity = qty,
                alertPrice = parts.getOrNull(4)?.toDoubleOrNull(),
                alertPnlPct = parts.getOrNull(5)?.toDoubleOrNull(),
            )
        }
        .associateBy { it.stockCode }
